import './index.css'
import React, { Component } from 'react'
import Immutable from 'immutable'
import debounce from 'lodash/debounce'
import classNames from 'classnames'

import AvatarGroup from 'components/Utils/AvatarGroup'
import BlockTypes from '@slite/editor/lib/types/BlockTypes'
import GutterBlockTypes from '@slite/editor/lib/types/GutterBlockTypes'
import makeGutterBlock from '@slite/editor/lib/plugins/GutterBlockPlugin/makeGutterBlock'

const ONE_MINUTE = 60 * 1000
// The TTL for the isWriting status display fades
const WRITE_STATUS_TTL = 5000
const WRITE_IDLE_TTL = 5 * ONE_MINUTE
const IDLE_TTL = 15 * ONE_MINUTE

function getNodeKey({ editor, block }) {
  const furthestParent = editor.value.document.getFurthestChild(block.key)

  return furthestParent && furthestParent.key
}

class PresenceBlock extends Component {
  writeStatusTimer = null

  state = {
    presences: [],
    blockTop: -1,
    blockRight: 0,
    showAvatar: true,
  }

  componentDidMount() {
    this.props.pluginStore.subscribeToItem('editorState', this.onEditorUpdate)

    this.updateUserPresences()
  }

  componentWillReceiveProps(nextProps) {
    if (!Immutable.is(this.props.userPresences, nextProps.userPresences)) {
      this.updateUserPresences(nextProps)
    }
  }

  componentWillUnmount() {
    this.props.pluginStore.unsubscribeFromItem(
      'editorState',
      this.onEditorUpdate
    )

    if (this.writeStatusTimer) {
      clearTimeout(this.writeStatusTimer)
      this.writeStatusTimer = null
    }
  }

  getPresenceScore = presence => {
    let score = new Date(presence.updatedAt).getTime()

    if (presence.isWriting) {
      // Keep the writing people first, whatever happens
      score += score
    }

    return score
  }

  sortPresences = presences => {
    return presences.sort(
      (a, b) => this.getPresenceScore(a) - this.getPresenceScore(b)
    )
  }

  resetWriteStatus = () => {
    this.writeStatusTimer = null

    const presences = this.state.presences.map(presence => {
      return { ...presence, isWriting: false, wroteAt: null }
    })
    this.setState({ presences })
  }

  launchWriteStatusResetTimeout = () => {
    clearTimeout(this.writeStatusTimer)

    this.writeStatusTimer = setTimeout(
      () => this.resetWriteStatus(),
      WRITE_STATUS_TTL
    )
  }

  updateUserPresences = (props = this.props) => {
    const { userPresences, currentUser } = props
    const presences = userPresences
      .filter(presence => {
        const timeFromLastActivity =
          Date.now() - new Date(presence.get('updatedAt'))
        const isCurrentUser = presence.get('id') === currentUser.get('id')
        const isRecent = timeFromLastActivity < IDLE_TTL

        return !isCurrentUser && isRecent
      })
      .toJS()

    const hasUsersWriting = presences.find(presence => {
      if (!presence.isWriting) {
        return false
      }
      const timeFromLastWrite = Date.now() - new Date(presence.wroteAt)
      return timeFromLastWrite < WRITE_IDLE_TTL
    })

    if (hasUsersWriting) {
      this.launchWriteStatusResetTimeout()
    }

    this.setState({
      presences,
      blockTop: this.getBlockTopOffset(props),
      blockRight: this.getBlockRightOffset(props),
    })
  }

  onEditorUpdate = debounce(
    editorState => {
      this.setState({
        blockTop: this.getBlockTopOffset(),
        blockRight: this.getBlockRightOffset(),
      })
    },
    20,
    { leading: true, trailing: false }
  )

  getBlockTopOffset = props => {
    const { editor, block } = props || this.props

    const editorBoundsNode = editor.el
    const editorBounds =
      editorBoundsNode && editorBoundsNode.getBoundingClientRect()

    const path = editor.value.document.getPath(block.key)
    const blockBoundsNode = editor.findDOMNode(path)
    const blockBounds =
      blockBoundsNode && blockBoundsNode.getBoundingClientRect()

    if (!editorBounds || !blockBounds) {
      return -1
    }

    return blockBounds.top - editorBounds.top
  }

  getBlockRightOffset = props => {
    const isBlockWithLeftStuff = [
      BlockTypes.TABLE_CELL,
      BlockTypes.CODE,
    ].includes((props || this.props).block.type)

    return isBlockWithLeftStuff ? 17 : 12
  }

  render() {
    const { blockTop, blockRight, presences } = this.state
    const { showGutterBlock } = this.props

    if (!Boolean(presences.length) || blockTop < 0) {
      return null
    }

    return (
      <div
        contentEditable={false}
        style={{ top: blockTop, right: `calc(100% + ${blockRight}px)` }}
        className={classNames(
          'slite-editor-presence-block position-absolute flexbox flexbox-column transition',
          !showGutterBlock && 'opacity-null'
        )}
      >
        <AvatarGroup
          size="small"
          borderColor="white"
          maxAvatars={2}
          users={presences}
        />
      </div>
    )
  }
}

export default makeGutterBlock({
  getNodeKey,
  gutterBlockType: GutterBlockTypes.BLOCK_PRESENCE,
})(PresenceBlock)
